import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import { Calendar, MessageCircle, Send, X } from "lucide-react";
import titiAvatar from "../../public/assets/titi-avatar.webp";
import { ADVISORY_FORM_PATH, CHAT_API_URL, SOCIAL } from "@/config/constants";
import { trackEvent } from "@/utils/analytics";
import type { CalendlyPrefill } from "@/utils/calendly";
import { useReducedMotion } from "@/utils/motion";
import { useAppTranslation } from "@/i18n/useAppTranslation";

type ChatMessage = {
  role: "user" | "assistant";
  content: string;
  booking?: CalendlyPrefill;
  error?: boolean;
};

const STORAGE_KEY = "lucentia_assistant_chat";
const OPENED_KEY = "lucentia_assistant_opened";
const TEASER_DELAY_MS = 4000;
const MOBILE_QUERY = "(max-width: 639px)";
const SESSION_KEY = "lucentia_assistant_session";
const MAX_INPUT = 1000;

function loadHistory(): ChatMessage[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as ChatMessage[]) : [];
  } catch {
    return [];
  }
}

/** Random id that groups this tab's messages into one conversation for lead tracking. */
function getConversationId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

function readFlag(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeFlag(key: string) {
  try {
    sessionStorage.setItem(key, "1");
  } catch {
    /* private mode / blocked storage */
  }
}

type ViewportBox = { top: number; height: number };

/**
 * On phones the chat is a full-screen sheet sized to the visual viewport, so
 * the header stays visible and the input sits right above the on-screen
 * keyboard instead of the browser panning the whole panel off screen.
 */
function useMobileSheet(open: boolean): ViewportBox | null {
  const [box, setBox] = useState<ViewportBox | null>(null);

  useEffect(() => {
    if (!open || !window.matchMedia(MOBILE_QUERY).matches) {
      setBox(null);
      return;
    }
    const vv = window.visualViewport;
    function update() {
      setBox({
        top: vv ? vv.offsetTop : 0,
        height: vv ? vv.height : window.innerHeight,
      });
    }
    update();
    vv?.addEventListener("resize", update);
    vv?.addEventListener("scroll", update);
    window.addEventListener("resize", update);

    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      vv?.removeEventListener("resize", update);
      vv?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      document.body.style.overflow = overflow;
    };
  }, [open]);

  return box;
}

function saveHistory(messages: ChatMessage[]) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-30)));
  } catch {
    /* private mode / blocked storage */
  }
}

export function ChatAssistant() {
  const reduced = useReducedMotion();
  const { t, i18n } = useAppTranslation("assistant");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>(loadHistory);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const [conversationId] = useState(getConversationId);
  const [hasOpened, setHasOpened] = useState(() => readFlag(OPENED_KEY));
  const [showTeaser, setShowTeaser] = useState(false);
  const sheet = useMobileSheet(open);

  useEffect(() => saveHistory(messages), [messages]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages, pending, open, sheet?.height]);

  /* Nudge first-time visitors with a short speech bubble. */
  useEffect(() => {
    if (hasOpened) return;
    const timer = window.setTimeout(() => setShowTeaser(true), TEASER_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [hasOpened]);

  useEffect(() => {
    if (!open) return;
    /* On phones, focusing would pop the keyboard over the quick questions. */
    if (!window.matchMedia(MOBILE_QUERY).matches) inputRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      setOpen(false);
      launcherRef.current?.focus();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  function openPanel() {
    setOpen(true);
    setShowTeaser(false);
    if (!hasOpened) {
      setHasOpened(true);
      writeFlag(OPENED_KEY);
    }
    trackEvent("assistant_open");
  }

  function dismissTeaser() {
    setShowTeaser(false);
    setHasOpened(true);
    writeFlag(OPENED_KEY);
  }

  function closePanel() {
    setOpen(false);
    launcherRef.current?.focus();
  }

  async function send(text: string) {
    const content = text.trim().slice(0, MAX_INPUT);
    if (!content || pending) return;

    const history: ChatMessage[] = [...messages, { role: "user", content }];
    setMessages(history);
    setInput("");
    setPending(true);

    try {
      const res = await fetch(CHAT_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          locale: i18n.language === "en" ? "en" : "es",
          session_id: conversationId,
          path: window.location.pathname,
          messages: history
            .filter((m) => !m.error)
            .map(({ role, content: c }) => ({ role, content: c })),
        }),
      });
      if (!res.ok) {
        throw new Error(res.status === 429 ? "rateLimited" : "error");
      }
      const data = (await res.json()) as { reply?: string; booking?: CalendlyPrefill };
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.reply ?? "", booking: data.booking },
      ]);
    } catch (err) {
      const key = err instanceof Error && err.message === "rateLimited" ? "rateLimited" : "error";
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: t(key), error: true, booking: {} },
      ]);
    } finally {
      setPending(false);
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    void send(input);
  }

  function handleBook(prefill: CalendlyPrefill) {
    trackEvent("calendly_click", { location: "assistant" });
    void fetch(CHAT_API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event: "booking_click", session_id: conversationId }),
      keepalive: true,
    }).catch(() => {
      /* fire-and-forget */
    });
    /* The calendar opens after the intake form; name and email carry over. */
    setOpen(false);
    navigate(ADVISORY_FORM_PATH, { state: { prefill } });
  }

  const suggestions = t("suggestions", { returnObjects: true }) as string[];

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.section
            role="dialog"
            aria-modal="false"
            aria-labelledby="assistant-title"
            style={sheet ? { top: sheet.top, height: sheet.height } : undefined}
            className="fixed z-[95] inset-x-0 top-0 h-[100dvh] flex flex-col overflow-hidden bg-white sm:z-[90] sm:inset-x-auto sm:top-auto sm:right-5 sm:bottom-24 sm:w-[24rem] sm:h-[min(36rem,calc(100dvh-8rem))] sm:rounded-2xl sm:border sm:border-brand-neutral sm:shadow-2xl sm:shadow-brand-primary/15"
            initial={reduced ? false : { opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduced ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.2 }}
          >
            <header className="flex shrink-0 items-center gap-3 px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:pt-3 bg-brand-primary text-white">
              <img
                src={titiAvatar}
                alt=""
                width={40}
                height={40}
                className="h-10 w-10 shrink-0 rounded-full bg-white object-cover ring-2 ring-white/30"
              />
              <div className="flex-1 min-w-0">
                <h2 id="assistant-title" className="font-display font-semibold leading-tight">
                  {t("title")}
                </h2>
                <p className="text-xs text-white/75">{t("status")}</p>
              </div>
              <button
                type="button"
                onClick={closePanel}
                aria-label={t("close")}
                className="rounded-full p-2 hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </header>

            <div
              ref={listRef}
              className="min-h-0 flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-brand-bg"
              aria-live="polite"
            >
              <Bubble role="assistant">{t("greeting")}</Bubble>

              {messages.map((m, i) => (
                <div key={i} className="space-y-2">
                  <Bubble role={m.role}>{m.content}</Bubble>
                  {m.booking && (
                    <div className="flex flex-wrap gap-2 pl-9">
                      <button
                        type="button"
                        onClick={() => handleBook(m.booking ?? {})}
                        className="inline-flex items-center gap-2 rounded-full bg-brand-secondary px-4 py-2 text-sm font-semibold text-brand-primary hover:bg-teal-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
                      >
                        <Calendar className="h-4 w-4" aria-hidden="true" />
                        {t("bookCta")}
                      </button>
                      {m.error && (
                        <a
                          href={SOCIAL.whatsapp}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-tracked=""
                          onClick={() => trackEvent("whatsapp_click", { location: "assistant" })}
                          className="inline-flex items-center gap-2 rounded-full border border-brand-primary/20 bg-white px-4 py-2 text-sm font-semibold text-brand-primary hover:bg-brand-primary/5"
                        >
                          <MessageCircle className="h-4 w-4" aria-hidden="true" />
                          {t("whatsapp")}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {pending && (
                <p className="text-sm text-ink-muted" role="status">
                  {t("thinking")}
                </p>
              )}

              {messages.length === 0 && !pending && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => void send(s)}
                      className="rounded-full border border-brand-secondary/40 bg-white px-3 py-1.5 text-sm text-brand-primary hover:bg-brand-secondary/10"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={handleSubmit}
              className="shrink-0 border-t border-brand-neutral bg-white px-3 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-3"
            >
              <div className="flex items-end gap-2">
                <label htmlFor="assistant-input" className="sr-only">
                  {t("inputLabel")}
                </label>
                <textarea
                  id="assistant-input"
                  ref={inputRef}
                  rows={1}
                  value={input}
                  maxLength={MAX_INPUT}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                      e.preventDefault();
                      void send(input);
                    }
                  }}
                  placeholder={t("inputPlaceholder")}
                  className="flex-1 resize-none max-h-28 rounded-xl border border-brand-neutral px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-brand-secondary"
                />
                <button
                  type="submit"
                  disabled={pending || !input.trim()}
                  aria-label={t("send")}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-primary text-white disabled:opacity-40 hover:bg-brand-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-secondary focus-visible:ring-offset-2"
                >
                  <Send className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
              <p className="mt-2 text-xs leading-snug text-ink-muted">
                {t("disclaimer")}{" "}
                <Link to="/privacidad" className="underline underline-offset-2 hover:text-brand-primary">
                  {t("privacyLink")}
                </Link>
              </p>
            </form>
          </motion.section>
        )}
      </AnimatePresence>

      <div
        className={`fixed z-[90] bottom-5 right-5 ${open ? "hidden sm:block" : "block"}`}
      >
        <AnimatePresence>
          {showTeaser && !open && (
            <motion.div
              className="absolute bottom-full right-0 mb-3 flex w-max max-w-[15rem] items-start gap-1 rounded-2xl rounded-br-md border border-brand-neutral bg-white py-2.5 pl-3.5 pr-1.5 text-sm text-slate-800 shadow-lg shadow-brand-primary/15"
              initial={reduced ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <button type="button" onClick={openPanel} className="text-left font-medium">
                {t("teaser")}
              </button>
              <button
                type="button"
                onClick={dismissTeaser}
                aria-label={t("teaserDismiss")}
                className="-mt-0.5 rounded-full p-1 text-slate-400 hover:bg-brand-bg hover:text-slate-600"
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {!hasOpened && !open && (
          <span
            aria-hidden="true"
            className="assistant-ping pointer-events-none absolute inset-0 rounded-full bg-brand-secondary"
          />
        )}

        <div className={!hasOpened && !open ? "assistant-wiggle" : undefined}>
          <button
            ref={launcherRef}
            type="button"
            onClick={open ? closePanel : openPanel}
            aria-label={open ? t("close") : t("launcher")}
            aria-expanded={open}
            className={`relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-full text-white shadow-lg shadow-brand-primary/30 transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-secondary focus-visible:ring-offset-2 ${
              open ? "bg-brand-primary" : "bg-white border-[3px] border-brand-primary"
            }`}
          >
            {open ? (
              <X className="h-6 w-6" aria-hidden="true" />
            ) : (
              <img src={titiAvatar} alt="" width={64} height={64} className="h-full w-full object-cover" />
            )}
          </button>
        </div>
      </div>
    </>
  );
}

function Bubble({ role, children }: { role: ChatMessage["role"]; children: string }) {
  if (role === "user") {
    return (
      <p className="ml-auto max-w-[85%] whitespace-pre-line rounded-2xl rounded-br-md bg-brand-primary px-3.5 py-2.5 text-sm leading-relaxed text-white">
        {children}
      </p>
    );
  }
  return (
    <div className="flex items-end gap-2">
      <img
        src={titiAvatar}
        alt=""
        width={28}
        height={28}
        className="h-7 w-7 shrink-0 rounded-full border border-brand-neutral bg-white object-cover"
      />
      <p className="max-w-[85%] whitespace-pre-line rounded-2xl rounded-bl-md border border-brand-neutral bg-white px-3.5 py-2.5 text-sm leading-relaxed text-slate-800">
        {children}
      </p>
    </div>
  );
}
